import {
  isHaklaiPre1997AcquisitionDateEligible,
  shouldUseHaklaiTestatorBoughtPropertyDate
} from "../preCalc/logic/haklaiPreCalcHelpers";
import { isHaklaiRabinovichEligible } from "../../domain/haklaiPrisaDependencies";
import { HAKLAI_PTOR_TYPES } from "./haklaiConstants";
import {
  getHaklaiLogicalPropertyPartKey,
  getHaklaiLogicalPropertyPartsData,
  getRenderablePropertyPartRows,
  isHaklaiPre1997MinimalZabanRelevantForPart,
  mergeHaklaiPartKeyedDepreciationProfile
} from "./haklaiFlowHelpers";
import {
  getHaklaiExpenseDepreciationMode,
  hasHaklaiManualForm11DepreciationInput,
  hasHaklaiSelectablePurchaseDepreciationProfile,
  HAKLAI_PROPERTY_DEPRECIATION_MODE,
  isHaklaiManualPurchaseDepreciationAllowed,
  normalizeHaklaiResidentialRentalByPart
} from "./haklaiDepreciationHelpers";
import {
  getHaklaiExpenseDepreciationDecision,
  getHaklaiExpenseTypeDepreciationPolicy,
  HAKLAI_EXPENSE_DEPRECIATION_DECISION,
  isHaklaiExpenseDepreciationPolicyRelevant,
  isHaklaiExpenseDepreciationStateMeaningful,
  normalizeHaklaiExpenseDepreciationDecisionState
} from "../expenses/logic/haklaiExpenseDepreciationPolicy";
import {
  normalizeHaklaiPropertyPartPtorSelections,
  sanitizeHaklaiPtorTypeForWay
} from "./haklaiPtorHelpers";

const getPlainObject = (value) => (
  value
  && typeof value === "object"
  && !Array.isArray(value)
    ? value
    : {}
);

const getExpenseTargetDepreciationByPart = (expense = {}) => {
  const targetPartKey = expense.targetPartKey;
  const depreciationByPart = getPlainObject(expense.depreciationByPart);
  if (
    !targetPartKey
    || !Object.prototype.hasOwnProperty.call(depreciationByPart, targetPartKey)
  ) {
    return {};
  }
  return { [targetPartKey]: depreciationByPart[targetPartKey] };
};

const getLogicalPartKeyForPayload = (
  partKey = "",
  propertyPartsData = {},
  logicalPropertyPartsData = {}
) => getHaklaiLogicalPropertyPartKey({
  partKey,
  propertyPartsData,
  logicalPropertyPartsData
});

const remapPartKeyedObjectToLogicalKeys = (
  source = {},
  propertyPartsData = {},
  logicalPropertyPartsData = {},
  mergeValues = null
) => Object.entries(getPlainObject(source)).reduce((acc, [partKey, value]) => {
  const logicalPartKey = getLogicalPartKeyForPayload(partKey, propertyPartsData, logicalPropertyPartsData);
  if (!logicalPartKey) return acc;
  acc[logicalPartKey] = typeof mergeValues === "function"
    ? mergeValues(acc[logicalPartKey], value)
    : value;
  return acc;
}, {});

const remapExpenseToLogicalTarget = (
  expense = {},
  propertyPartsData = {},
  logicalPropertyPartsData = {}
) => {
  const targetPartKey = getLogicalPartKeyForPayload(
    expense?.targetPartKey,
    propertyPartsData,
    logicalPropertyPartsData
  );
  if (!targetPartKey) return null;

  return {
    ...expense,
    targetPartKey,
    depreciationByPart: remapPartKeyedObjectToLogicalKeys(
      expense?.depreciationByPart,
      propertyPartsData,
      logicalPropertyPartsData,
      mergeHaklaiPartKeyedDepreciationProfile
    )
  };
};

export const sanitizePre1997ConstructionFlags = (propertyPartsData = {}, preCalc = {}) => {
  let changed = false;
  const hasZaban = preCalc?.hasZaban === true;
  const acquisitionDateEligible = isHaklaiPre1997AcquisitionDateEligible(preCalc);

  const nextParts = Object.entries(propertyPartsData || {}).reduce((acc, [partKey, part]) => {
    if (
      part?.constructionsDoneBeforeApril1997 === true
      && (
        !acquisitionDateEligible
        || !isHaklaiPre1997MinimalZabanRelevantForPart(part, hasZaban)
      )
    ) {
      changed = true;
      acc[partKey] = { ...part, constructionsDoneBeforeApril1997: false };
      return acc;
    }

    acc[partKey] = part;
    return acc;
  }, {});

  return {
    changed,
    propertyPartsData: changed ? nextParts : propertyPartsData
  };
};

const sanitizeExpenseLevelDepreciation = (
  expenses = [],
  propertyPartsData = {},
  logicalPropertyPartsData = {},
  residentialRentalByPart = {}
) => {
  const activePartKeys = new Set(
    getRenderablePropertyPartRows(logicalPropertyPartsData).map(([partKey]) => partKey)
  );

  return (Array.isArray(expenses) ? expenses : [])
    .filter((expense = {}) => expense.expenseScope !== "globalMeshek")
    .map((expense = {}) => remapExpenseToLogicalTarget(expense, propertyPartsData, logicalPropertyPartsData))
    .filter((expense = {}) => expense && activePartKeys.has(expense.targetPartKey))
    .map((expense = {}) => {
      const nextExpense = { ...expense };
      delete nextExpense.isDepreciable;
      delete nextExpense.depreciation;
      const policy = getHaklaiExpenseTypeDepreciationPolicy(nextExpense.expenseType || "");
      const nextDepreciationByPart = Object.entries(getExpenseTargetDepreciationByPart(nextExpense))
        .reduce((acc, [partKey, depreciation]) => {
          const part = logicalPropertyPartsData?.[partKey];
          const mode = getHaklaiExpenseDepreciationMode(part);
          const decision = getHaklaiExpenseDepreciationDecision(depreciation);
          const hasRequiredProfile = mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
            ? Boolean(residentialRentalByPart?.[partKey])
            : mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11;
          const isCompatibleWithMode = (
            (
              decision === HAKLAI_EXPENSE_DEPRECIATION_DECISION.RESIDENTIAL_AUTO
              && mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
            )
            || (
              decision === HAKLAI_EXPENSE_DEPRECIATION_DECISION.FORM_11
              && mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11
            )
          );
          if (
            partKey
            && part
            && hasRequiredProfile
            && isHaklaiExpenseDepreciationPolicyRelevant(policy)
            && isCompatibleWithMode
            && isHaklaiExpenseDepreciationStateMeaningful(depreciation)
          ) {
            acc[partKey] = normalizeHaklaiExpenseDepreciationDecisionState(depreciation);
          }
          return acc;
        }, {});
      if (Object.keys(nextDepreciationByPart).length > 0) {
        nextExpense.depreciationByPart = nextDepreciationByPart;
      } else {
        delete nextExpense.depreciationByPart;
      }
      return nextExpense;
    });
};

const sanitizePurchaseDepreciationByPart = (
  purchaseDepreciationByPart = {},
  propertyPartsData = {},
  logicalPropertyPartsData = {}
) => {
  const depreciationByPart = (
    purchaseDepreciationByPart
    && typeof purchaseDepreciationByPart === "object"
    && !Array.isArray(purchaseDepreciationByPart)
  ) ? purchaseDepreciationByPart : {};

  return Object.entries(depreciationByPart).reduce((acc, [partKey, depreciation]) => {
    const logicalPartKey = getLogicalPartKeyForPayload(partKey, propertyPartsData, logicalPropertyPartsData);
    const part = logicalPropertyPartsData?.[logicalPartKey];
    if (
      !part
      || !isHaklaiManualPurchaseDepreciationAllowed(part)
    ) {
      return acc;
    }
    if (
      depreciation
      && typeof depreciation === "object"
      && !Array.isArray(depreciation)
      && hasHaklaiManualForm11DepreciationInput(depreciation)
    ) {
      acc[logicalPartKey] = mergeHaklaiPartKeyedDepreciationProfile(acc[logicalPartKey], depreciation);
    }
    return acc;
  }, {});
};

const sanitizeResidentialRentalByPart = (
  residentialRentalByPart = {},
  propertyPartsData = {}
) => normalizeHaklaiResidentialRentalByPart(
  residentialRentalByPart,
  propertyPartsData
);

const sanitizePropertyPartsForRawPayload = (propertyPartsData = {}) => {
  if (!propertyPartsData || typeof propertyPartsData !== "object" || Array.isArray(propertyPartsData)) {
    return {};
  }

  return Object.entries(propertyPartsData).reduce((acc, [partKey, part]) => {
    if (!part || typeof part !== "object" || Array.isArray(part)) {
      acc[partKey] = part;
      return acc;
    }

    const nextPart = { ...part };
    delete nextPart.isRented;
    delete nextPart.zabanAmount;
    acc[partKey] = nextPart;
    return acc;
  }, {});
};

const sanitizeDraftExpensesForRawPayload = (expenses = []) => (
  (Array.isArray(expenses) ? expenses : [])
    .filter((expense = {}) => expense.expenseScope !== "globalMeshek")
    .map((expense = {}) => {
      const nextExpense = { ...expense };
      delete nextExpense.isDepreciable;
      delete nextExpense.depreciation;
      const nextDepreciationByPart = getExpenseTargetDepreciationByPart(nextExpense);
      if (Object.keys(nextDepreciationByPart).length > 0) {
        nextExpense.depreciationByPart = nextDepreciationByPart;
      } else {
        delete nextExpense.depreciationByPart;
      }
      return nextExpense;
    })
);

const sanitizePurchaseDepreciationSelectionByPart = ({
  purchaseDepreciationSelectionByPart = {},
  propertyPartsData = {},
  logicalPropertyPartsData = {},
  residentialRentalByPart = {},
  purchaseDepreciationByPart = {}
} = {}) => {
  const selectionByPart = (
    purchaseDepreciationSelectionByPart
    && typeof purchaseDepreciationSelectionByPart === "object"
    && !Array.isArray(purchaseDepreciationSelectionByPart)
  ) ? purchaseDepreciationSelectionByPart : {};

  return Object.entries(selectionByPart).reduce((acc, [partKey, isSelected]) => {
    const logicalPartKey = getLogicalPartKeyForPayload(partKey, propertyPartsData, logicalPropertyPartsData);
    const part = logicalPropertyPartsData?.[logicalPartKey];
    const hasPurchaseProfile = hasHaklaiSelectablePurchaseDepreciationProfile({
      partKey: logicalPartKey,
      part,
      residentialRentalByPart,
      purchaseDepreciationByPart
    });
    if (isSelected === true && part && hasPurchaseProfile) {
      acc[logicalPartKey] = true;
    }
    return acc;
  }, {});
};

const sanitizeAcquisitionDetailsForWay = (preCalc = {}) => {
  const acquisitionDetails = { ...(preCalc.acquisitionDetails || {}) };
  const clearIfPresent = (fieldName) => {
    if (Object.prototype.hasOwnProperty.call(acquisitionDetails, fieldName)) {
      acquisitionDetails[fieldName] = "";
    }
  };

  if (preCalc.wayOfGettingProperty === "inheritance") {
    clearIfPresent("deduDate");
    if (!shouldUseHaklaiTestatorBoughtPropertyDate(acquisitionDetails.testatorPassingDate)) {
      clearIfPresent("testatorBoughtPropertyDate");
    }
    return acquisitionDetails;
  }

  clearIfPresent("testatorPassingDate");
  clearIfPresent("testatorBoughtPropertyDate");
  return acquisitionDetails;
};

const sanitizePtorTypeForWay = (preCalc = {}) => {
  return sanitizeHaklaiPtorTypeForWay(
    preCalc.ptorType,
    preCalc.wayOfGettingProperty
  );
};

export const buildHaklaiRawPayload = (sourceCalc = {}) => {
  const calcToSend = JSON.parse(JSON.stringify(sourceCalc || {}));
  calcToSend.mainCalc = calcToSend.mainCalc || {};
  delete calcToSend.mainCalc.isGilum;
  if (calcToSend.preCalc) {
    calcToSend.preCalc.acquisitionDetails = sanitizeAcquisitionDetailsForWay(calcToSend.preCalc);
    calcToSend.preCalc.acquisitionDetails.hasRabinovich = (
      calcToSend.preCalc.acquisitionDetails.hasRabinovich === true
      && isHaklaiRabinovichEligible(calcToSend.preCalc)
    );
    calcToSend.preCalc.parcelLookupError = null;
    delete calcToSend.preCalc.cosnstructionsDoneBeforeApril1997;
  }
  calcToSend.mainCalc.propertyPartsData = sanitizePropertyPartsForRawPayload(
    calcToSend.mainCalc.propertyPartsData || {}
  );
  const ptorState = normalizeHaklaiPropertyPartPtorSelections(
    calcToSend.mainCalc.propertyPartsData || {},
    {
      ...calcToSend.preCalc,
      ptorType: sanitizePtorTypeForWay(calcToSend.preCalc || {})
    }
  );
  calcToSend.mainCalc.propertyPartsData = ptorState.propertyPartsData;
  if (calcToSend.preCalc) {
    calcToSend.preCalc.ptorType = ptorState.selectedPtorType;
    if (!(
      calcToSend.preCalc.wayOfGettingProperty === "gift"
      && calcToSend.preCalc.ptorType === HAKLAI_PTOR_TYPES.SINGLE
    )) {
      calcToSend.preCalc.hasTzinunOnPtor = false;
    }
  }
  const sanitizedPre1997State = sanitizePre1997ConstructionFlags(
    calcToSend.mainCalc.propertyPartsData || {},
    calcToSend.preCalc || {}
  );
  if (sanitizedPre1997State.changed) {
    calcToSend.mainCalc.propertyPartsData = sanitizedPre1997State.propertyPartsData;
  }
  const rawPropertyPartsData = calcToSend.mainCalc.propertyPartsData || {};
  const logicalPropertyPartsData = getHaklaiLogicalPropertyPartsData(
    rawPropertyPartsData,
    calcToSend.preCalc?.totalPropertyCost,
    calcToSend.preCalc?.hasZaban === true
  );
  calcToSend.mainCalc.residentialRentalByPart = sanitizeResidentialRentalByPart(
    calcToSend.mainCalc.residentialRentalByPart,
    logicalPropertyPartsData
  );
  calcToSend.mainCalc.purchaseDepreciationByPart = sanitizePurchaseDepreciationByPart(
    calcToSend.mainCalc.purchaseDepreciationByPart,
    rawPropertyPartsData,
    logicalPropertyPartsData
  );
  calcToSend.mainCalc.purchaseDepreciationSelectionByPart = sanitizePurchaseDepreciationSelectionByPart({
    purchaseDepreciationSelectionByPart: calcToSend.mainCalc.purchaseDepreciationSelectionByPart,
    propertyPartsData: rawPropertyPartsData,
    logicalPropertyPartsData,
    residentialRentalByPart: calcToSend.mainCalc.residentialRentalByPart,
    purchaseDepreciationByPart: calcToSend.mainCalc.purchaseDepreciationByPart
  });
  calcToSend.mainCalc.purchaseZabanAttributionByPart = {};
  if (Array.isArray(calcToSend.mainCalc.expenses)) {
    calcToSend.mainCalc.expenses = sanitizeExpenseLevelDepreciation(
      calcToSend.mainCalc.expenses,
      rawPropertyPartsData,
      logicalPropertyPartsData,
      calcToSend.mainCalc.residentialRentalByPart
    );
  }
  calcToSend.mainCalc.propertyPartsData = logicalPropertyPartsData;
  if (!calcToSend.mainCalc.prisa?.shouldTryPrisa) {
    calcToSend.mainCalc.prisa = { shouldTryPrisa: false, personsData: [] };
  }
  return calcToSend;
};

export const buildHaklaiDraftRawPayload = (sourceCalc = {}) => {
  const calcToSend = JSON.parse(JSON.stringify(sourceCalc || {}));
  calcToSend.mainCalc = calcToSend.mainCalc || {};
  delete calcToSend.mainCalc.isGilum;
  if (calcToSend.preCalc) {
    calcToSend.preCalc.ptorType = sanitizePtorTypeForWay(calcToSend.preCalc);
    calcToSend.preCalc.parcelLookupError = null;
    delete calcToSend.preCalc.cosnstructionsDoneBeforeApril1997;
  }
  calcToSend.mainCalc.propertyPartsData = sanitizePropertyPartsForRawPayload(
    calcToSend.mainCalc.propertyPartsData || {}
  );
  const ptorState = normalizeHaklaiPropertyPartPtorSelections(
    calcToSend.mainCalc.propertyPartsData || {},
    calcToSend.preCalc || {}
  );
  calcToSend.mainCalc.propertyPartsData = ptorState.propertyPartsData;
  if (calcToSend.preCalc) {
    calcToSend.preCalc.ptorType = ptorState.selectedPtorType;
    if (!(
      calcToSend.preCalc.wayOfGettingProperty === "gift"
      && calcToSend.preCalc.ptorType === HAKLAI_PTOR_TYPES.SINGLE
    )) {
      calcToSend.preCalc.hasTzinunOnPtor = false;
    }
  }
  if (Array.isArray(calcToSend.mainCalc.expenses)) {
    calcToSend.mainCalc.expenses = sanitizeDraftExpensesForRawPayload(calcToSend.mainCalc.expenses);
  }
  if (!calcToSend.mainCalc.prisa?.shouldTryPrisa) {
    calcToSend.mainCalc.prisa = { shouldTryPrisa: false, personsData: [] };
  }
  return calcToSend;
};
